/**
 * MGM v1 · capture and attribution in the browser (Phase 3).
 *
 * 1. captureArrival() runs in main.tsx before the router, so no redirect can drop the
 *    query string. Only a URL with ref or utm_* is captured: the browser keeps a
 *    random visitor id and the code for 90 days and tells the server (acq-touch).
 *    No IP, cookie or fingerprint; a visit without ref or UTM leaves no trace.
 * 2. queueAttribution() runs right after signup. It stores what the founder chose in
 *    the form (typed code, "Remover", "Como conheceu?") with the visitor id, tied to
 *    the signup e-mail by a hash, so another account on the same browser never
 *    sends it.
 * 3. flushAttribution() calls acq_attribute_office as the signed-in founder. It never
 *    blocks the signup: the request outlives a redirect (keepalive), and if it fails
 *    the entry stays and the next signed-in page load tries again, including the
 *    return from Stripe. The server is idempotent and anchors the evidence to the
 *    signup, so a late or repeated call reaches the same decision.
 *
 * The browser never decides a referrer and never learns whether a code exists.
 * Nothing here throws: storage, crypto and network failures only skip the step.
 */

import { ACQ_CAPTURE_PARAMS, ACQ_LOOKBACK_DAYS, ACQ_SELF_REPORTED, normalizeAcqCode, type AcqSelfReported } from '../constants/acquisition'

export const ARRIVAL_KEY = 'is.acq.arrival'
export const PENDING_KEY = 'is.acq.pending'
const DAY_MS = 24 * 60 * 60 * 1000
const MAX = { ref: 20, utm: 100, referrer: 300, path: 200, typed: 40 }

export interface Arrival {
  v: 1
  visitorId: string
  /** Normalized code the link applied, or null (UTM only, or a malformed ref). */
  code: string | null
  capturedAt: number
}

export interface PendingAttribution {
  v: 1
  emailHash: string
  visitorId: string | null
  typedCode: string | null
  linkDeclined: boolean
  selfReported: AcqSelfReported | null
  createdAt: number
  attempts: number
}

export type FlushResult = 'none' | 'skipped' | 'recorded' | 'already_recorded' | 'not_eligible' | 'failed'

/** The subset of Storage this module uses; localStorage in the app, a map in tests. */
export interface KeyValueStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function browserStore(): KeyValueStore | null {
  try { return typeof window !== 'undefined' ? window.localStorage : null } catch { return null }
}

function readJson<T>(store: KeyValueStore | null, key: string): T | null {
  if (!store) return null
  try {
    const raw = store.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch { return null }
}

function writeJson(store: KeyValueStore | null, key: string, value: unknown): boolean {
  if (!store) return false
  try { store.setItem(key, JSON.stringify(value)); return true } catch { return false }
}

function remove(store: KeyValueStore | null, key: string) {
  if (!store) return
  try { store.removeItem(key) } catch { /* nothing to do */ }
}

function clip(v: string | null, max: number): string | null {
  if (v == null) return null
  // eslint-disable-next-line no-control-regex
  const s = v.replace(/[\u0000-\u001F\u007F]+/g, '').trim()
  return s ? s.slice(0, max) : null
}

function newVisitorId(): string | null {
  try { return crypto.randomUUID() } catch { return null }
}

// ── 1. Arrival ──────────────────────────────────────────────────────────────

/** The stored arrival, or null when there is none or it is older than 90 days. */
export function readArrival(store: KeyValueStore | null = browserStore(), now = Date.now()): Arrival | null {
  const a = readJson<Arrival>(store, ARRIVAL_KEY)
  if (!a || a.v !== 1 || typeof a.visitorId !== 'string' || typeof a.capturedAt !== 'number') return null
  if (now - a.capturedAt > ACQ_LOOKBACK_DAYS * DAY_MS) {
    remove(store, ARRIVAL_KEY)
    return null
  }
  return a
}

/** The code the link applied, for the "Código de indicação K7M2-Q9XA" line in the form. */
export function appliedCode(store: KeyValueStore | null = browserStore(), now = Date.now()): string | null {
  return readArrival(store, now)?.code ?? null
}

export interface TouchBody {
  visitor_id: string
  ref: string | null
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  utm_content: string | null
  utm_term: string | null
  referrer: string | null
  path: string | null
}

export interface CaptureDeps {
  search: string
  pathname: string
  referrer?: string
  store?: KeyValueStore | null
  now?: number
  send?: (body: TouchBody) => void
}

function sendTouch(body: TouchBody) {
  const base = import.meta.env.VITE_SUPABASE_URL as string | undefined
  if (!base) return
  try {
    void fetch(`${base}/functions/v1/acq-touch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => { /* the visit never depends on this call */ })
  } catch { /* idem */ }
}

/**
 * Captures ?ref= and utm_* from the landing URL. Returns the stored arrival, or null
 * when the URL carries neither (nothing is stored or sent in that case).
 */
export function captureArrival(deps: CaptureDeps): Arrival | null {
  const params = new URLSearchParams(deps.search)
  const got: Record<string, string | null> = {}
  for (const p of ACQ_CAPTURE_PARAMS) got[p] = clip(params.get(p), p === 'ref' ? MAX.ref : MAX.utm)
  if (!ACQ_CAPTURE_PARAMS.some((p) => got[p])) return null

  const store = deps.store === undefined ? browserStore() : deps.store
  const now = deps.now ?? Date.now()
  const previous = readArrival(store, now)
  const visitorId = previous?.visitorId ?? newVisitorId()
  if (!visitorId) return null

  // The most recent valid code wins; a malformed ref keeps the code already stored.
  const arrival: Arrival = { v: 1, visitorId, code: normalizeAcqCode(got.ref) ?? previous?.code ?? null, capturedAt: now }
  writeJson(store, ARRIVAL_KEY, arrival)

  ;(deps.send ?? sendTouch)({
    visitor_id: visitorId,
    ref: got.ref,
    utm_source: got.utm_source,
    utm_medium: got.utm_medium,
    utm_campaign: got.utm_campaign,
    utm_content: got.utm_content,
    utm_term: got.utm_term,
    referrer: clip(deps.referrer ?? null, MAX.referrer),
    path: clip(deps.pathname, MAX.path),
  })
  return arrival
}

// ── 2. Queue after signup ───────────────────────────────────────────────────

/** SHA-256 of the normalized e-mail; FNV-1a where WebCrypto is unavailable. */
export async function emailHash(email: string): Promise<string> {
  const v = email.trim().toLowerCase()
  try {
    const d = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(v))
    return Array.from(new Uint8Array(d), (b) => b.toString(16).padStart(2, '0')).join('')
  } catch {
    let h = 0x811c9dc5
    for (let i = 0; i < v.length; i++) { h ^= v.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0 }
    return `fnv1a:${h.toString(16)}`
  }
}

export interface QueueInput {
  email: string
  typedCode?: string | null
  linkDeclined?: boolean
  selfReported?: string | null
}

/** Stores the founder's choices for the attribution call. One entry: the latest signup. */
export async function queueAttribution(input: QueueInput, store: KeyValueStore | null = browserStore(), now = Date.now()): Promise<PendingAttribution | null> {
  const arrival = readArrival(store, now)
  const self = (ACQ_SELF_REPORTED as readonly string[]).includes(input.selfReported ?? '') ? (input.selfReported as AcqSelfReported) : null
  const pending: PendingAttribution = {
    v: 1,
    emailHash: await emailHash(input.email),
    visitorId: arrival?.visitorId ?? null,
    typedCode: clip(input.typedCode ?? null, MAX.typed),
    // Only meaningful when the link had applied a code.
    linkDeclined: Boolean(input.linkDeclined && arrival?.code),
    selfReported: self,
    createdAt: now,
    attempts: 0,
  }
  return writeJson(store, PENDING_KEY, pending) ? pending : null
}

export function readPending(store: KeyValueStore | null = browserStore()): PendingAttribution | null {
  const p = readJson<PendingAttribution>(store, PENDING_KEY)
  return p && p.v === 1 && typeof p.emailHash === 'string' ? p : null
}

// ── 3. Send ─────────────────────────────────────────────────────────────────

export interface FlushDeps {
  email: string | null | undefined
  accessToken: string | null | undefined
  store?: KeyValueStore | null
  supabaseUrl?: string
  anonKey?: string
  fetchImpl?: typeof fetch
}

const TERMINAL = new Set(['recorded', 'already_recorded', 'not_eligible'])
let inFlight: Promise<FlushResult> | null = null

async function doFlush(deps: FlushDeps): Promise<FlushResult> {
  const store = deps.store === undefined ? browserStore() : deps.store
  const pending = readPending(store)
  if (!pending) return 'none'
  if (!deps.email || !deps.accessToken) return 'skipped'
  if ((await emailHash(deps.email)) !== pending.emailHash) return 'skipped'

  const base = deps.supabaseUrl ?? (import.meta.env.VITE_SUPABASE_URL as string | undefined)
  const anon = deps.anonKey ?? (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined)
  if (!base || !anon) return 'failed'
  try {
    const res = await (deps.fetchImpl ?? fetch)(`${base}/rest/v1/rpc/acq_attribute_office`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: anon, Authorization: `Bearer ${deps.accessToken}` },
      body: JSON.stringify({
        p_visitor_id: pending.visitorId,
        p_typed_code: pending.typedCode,
        p_self_reported: pending.selfReported,
        p_link_declined: pending.linkDeclined,
      }),
      // A signup that goes straight to Stripe still delivers the call.
      keepalive: true,
    })
    const status = res.ok ? ((await res.json().catch(() => null)) as { status?: string } | null)?.status : undefined
    if (status && TERMINAL.has(status)) {
      // Clear only the entry that was sent (a newer signup may have replaced it).
      if (readPending(store)?.createdAt === pending.createdAt) remove(store, PENDING_KEY)
      return status as FlushResult
    }
  } catch { /* network or abort: keep the entry */ }
  const current = readPending(store)
  if (current?.createdAt === pending.createdAt) writeJson(store, PENDING_KEY, { ...current, attempts: current.attempts + 1 })
  return 'failed'
}

/** Sends the queued attribution if it belongs to this user. One call at a time per tab. */
export function flushAttribution(deps: FlushDeps): Promise<FlushResult> {
  if (inFlight) return inFlight
  inFlight = doFlush(deps).finally(() => { inFlight = null })
  return inFlight
}
